import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Building2 } from "lucide-react";

interface Branch {
  id: string;
  name: string;
}

interface BulkBranchAssignmentProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedIds: string[];
  branches: Branch[];
  tableName: "students" | "instructors";
  onComplete: () => void;
}

export const BulkBranchAssignment = ({
  open,
  onOpenChange,
  selectedIds,
  branches,
  tableName,
  onComplete
}: BulkBranchAssignmentProps) => {
  const [selectedBranch, setSelectedBranch] = useState<string>("");
  const [loading, setLoading] = useState(false);

  const handleAssign = async () => {
    if (!selectedBranch) {
      toast.error("Please select a branch");
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase
        .from(tableName)
        .update({ branch_id: selectedBranch })
        .in("id", selectedIds);

      if (error) throw error;

      toast.success(`${selectedIds.length} ${tableName === "students" ? "students" : "instructors"} assigned to branch`);
      onOpenChange(false);
      setSelectedBranch("");
      onComplete();
    } catch (error: any) {
      toast.error(error.message || "Failed to assign branch");
    } finally {
      setLoading(false);
    }
  };

  // Filter to only show branch-type batches (IC Calicut, IC Bangalore)
  const branchOptions = branches.filter(b => 
    b.name.startsWith("IC ") || b.name.includes("Calicut") || b.name.includes("Bangalore")
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Assign to Branch
          </DialogTitle>
          <DialogDescription>
            Assign {selectedIds.length} selected {tableName === "students" ? "student" : "instructor"}{selectedIds.length !== 1 ? "s" : ""} to a branch
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Select Branch</Label>
            <Select value={selectedBranch} onValueChange={setSelectedBranch}>
              <SelectTrigger>
                <SelectValue placeholder="Choose a branch" />
              </SelectTrigger>
              <SelectContent>
                {branchOptions.map(branch => (
                  <SelectItem key={branch.id} value={branch.id}>
                    {branch.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleAssign} disabled={loading || !selectedBranch}>
            {loading ? "Assigning..." : "Assign to Branch"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
